import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { Car } from 'lucide-react';
import { RuleTag } from '@/components/RuleTag';

/**
 * MvcSubSet — Foundation v1.0 §4.5 (Motor Vehicle Crash DOT Data Capture).
 *
 * Captured whenever eInjury.01 contains an MVC cause. Feeds:
 *   - NHTSA crash-surveillance registry
 *   - NEMSIS trauma registry
 *   - Auto-population of eInjury.04 yellow triage criteria (intrusion,
 *     ejection, death in compartment, rollover)
 *
 * Kept deliberately scannable: two-column form, one tap per option
 * for restraints + airbags + high-risk indicators (common case on an
 * iPad under pressure).
 */

interface Props {
  epcr: EPCR;
  onUpdate: (patch: Partial<EPCR>) => void;
}

const VEHICLE_TYPES = ['Car', 'Truck', 'SUV', 'Bus', 'Motorcycle', 'Bicycle', 'ATV', 'Other'];
const PATIENT_ROLES = ['Driver', 'Passenger', 'Pedestrian', 'Cyclist', 'Motorcyclist'];
const SEAT_POSITIONS = ['Front-L', 'Front-R', 'Rear-L', 'Rear-M', 'Rear-R', 'Other'];

const RESTRAINTS = [
  'Lap belt',
  'Shoulder belt',
  'Lap and shoulder belt',
  'Child safety seat',
  'Booster seat',
  'Helmet',
  'None used',
  'Unknown',
];

const AIRBAGS = ['Front', 'Side', 'Curtain', 'Knee', 'None deployed', 'Unknown'];

const HIGH_RISK_INDICATORS = [
  'Intrusion > 12" at occupant site',
  'Intrusion > 18" at any site',
  'Ejection (partial or complete)',
  'Death in same passenger compartment',
  'Rollover',
  'Vehicle telemetry consistent with severe injury',
  'Significant vehicle deformation',
];

export function MvcSubSet({ epcr, onUpdate }: Props) {
  const locked = epcr.locked;

  const restraints = new Set(epcr.mvc_restraints ?? []);
  const airbags = new Set(epcr.mvc_airbags ?? []);
  const indicators = new Set(epcr.mvc_high_risk_indicators ?? []);

  function toggleSetValue(
    field: 'mvc_restraints' | 'mvc_airbags' | 'mvc_high_risk_indicators',
    value: string,
  ) {
    const current = new Set((epcr[field] as string[] | undefined) ?? []);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    onUpdate({ [field]: Array.from(current) } as Partial<EPCR>);
  }

  const extricationDurationMin = (() => {
    if (!epcr.mvc_extrication_start || !epcr.mvc_extrication_end) return null;
    const s = new Date(epcr.mvc_extrication_start).getTime();
    const e = new Date(epcr.mvc_extrication_end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return null;
    return Math.round((e - s) / 60_000);
  })();

  return (
    <section className="border border-blue-800/60 bg-blue-950/10">
      <header className="flex items-center justify-between border-b border-blue-800/40 bg-blue-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <Car size={14} className="text-blue-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-blue-200">
            MVC Sub-Set · DOT Data Capture
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'NEMSIS', rationale: 'MVC-specific fields for trauma registry' },
            { standard: 'Department', rationale: 'NHTSA crash-surveillance dataset' },
          ]}
        />
      </header>
      <div className="p-3 space-y-4">
        {/* Vehicle Info */}
        <Group title="Vehicle">
          <SelectField
            label="Vehicle Type"
            options={VEHICLE_TYPES}
            value={epcr.mvc_vehicle_type ?? ''}
            disabled={locked}
            onChange={(v) => onUpdate({ mvc_vehicle_type: v || null })}
          />
          <SelectField
            label="Patient Role"
            options={PATIENT_ROLES}
            value={epcr.mvc_patient_role ?? ''}
            disabled={locked}
            onChange={(v) => onUpdate({ mvc_patient_role: v || null })}
          />
          <SelectField
            label="Seat Position"
            options={SEAT_POSITIONS}
            value={epcr.mvc_seat_position ?? ''}
            disabled={locked}
            onChange={(v) => onUpdate({ mvc_seat_position: v || null })}
          />
        </Group>

        {/* Restraints */}
        <Group title="Restraints (select all that apply)">
          <Chips
            options={RESTRAINTS}
            selected={restraints}
            disabled={locked}
            onToggle={(v) => toggleSetValue('mvc_restraints', v)}
          />
        </Group>

        {/* Airbags */}
        <Group title="Airbag Deployment">
          <Chips
            options={AIRBAGS}
            selected={airbags}
            disabled={locked}
            onToggle={(v) => toggleSetValue('mvc_airbags', v)}
          />
        </Group>

        {/* High-risk indicators */}
        <Group title="High-Risk Crash Indicators">
          <p className="text-[10px] text-slate-400 mb-1.5 leading-snug">
            These auto-populate eInjury.04 yellow triage criteria.
          </p>
          <div className="space-y-1">
            {HIGH_RISK_INDICATORS.map((v) => (
              <IndicatorRow
                key={v}
                label={v}
                selected={indicators.has(v)}
                disabled={locked}
                onToggle={() => toggleSetValue('mvc_high_risk_indicators', v)}
              />
            ))}
          </div>
        </Group>

        {/* Extrication */}
        <Group title="Extrication">
          <div className="grid gap-2 sm:grid-cols-[auto_1fr_1fr_auto]">
            <ToggleYesNo
              label="Required?"
              value={epcr.mvc_extrication_required ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_extrication_required: v })}
            />
            <DateTimeField
              label="Start"
              value={epcr.mvc_extrication_start ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_extrication_start: v })}
            />
            <DateTimeField
              label="Complete"
              value={epcr.mvc_extrication_end ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_extrication_end: v })}
            />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Duration</div>
              <div className="font-mono text-slate-200 text-sm mt-1">
                {extricationDurationMin !== null ? `${extricationDurationMin} min` : '—'}
              </div>
            </div>
          </div>
        </Group>

        {/* Speed + Flags */}
        <Group title="Speed + Additional Flags">
          <div className="grid gap-2 sm:grid-cols-4">
            <NumberField
              label="Est. Speed (mph)"
              value={epcr.mvc_estimated_speed_mph ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_estimated_speed_mph: v })}
            />
            <NumberField
              label="Speed Limit (mph)"
              value={epcr.mvc_speed_limit_mph ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_speed_limit_mph: v })}
            />
            <ToggleYesNo
              label="Rollover"
              value={epcr.mvc_rollover ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_rollover: v })}
            />
            <ToggleYesNo
              label="Other Fatalities"
              value={epcr.mvc_other_fatalities ?? null}
              disabled={locked}
              onChange={(v) => onUpdate({ mvc_other_fatalities: v })}
            />
          </div>
        </Group>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function SelectField({
  label, options, value, disabled, onChange,
}: {
  label: string; options: string[]; value: string; disabled: boolean; onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 flex-1 min-w-[120px]">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="input-field text-sm"
      >
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function NumberField({
  label, value, disabled, onChange,
}: {
  label: string; value: number | null; disabled: boolean; onChange: (v: number | null) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        value={value ?? ''}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(e.target.value === '' ? null : Number.isFinite(n) ? n : null);
        }}
        disabled={disabled}
        className="input-field font-mono text-sm"
      />
    </label>
  );
}

function DateTimeField({
  label, value, disabled, onChange,
}: {
  label: string; value: string | null; disabled: boolean; onChange: (v: string | null) => void;
}) {
  // Convert ISO to datetime-local
  const local = value ? toLocal(value) : '';
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <div className="flex gap-1">
        <input
          type="datetime-local"
          value={local}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) { onChange(null); return; }
            onChange(new Date(v).toISOString());
          }}
          disabled={disabled}
          className="input-field flex-1 text-sm"
        />
        <button
          type="button"
          onClick={() => onChange(new Date().toISOString())}
          disabled={disabled}
          className="px-2 border border-slate-600 bg-slate-800/80 text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700"
          title="Set to current time"
        >
          Now
        </button>
      </div>
    </label>
  );
}

function ToggleYesNo({
  label, value, disabled, onChange,
}: {
  label: string; value: boolean | null; disabled: boolean; onChange: (v: boolean | null) => void;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 flex gap-1">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(value === v ? null : v)}
            disabled={disabled}
            className={cn(
              'px-3 py-1 text-xs font-bold uppercase tracking-widest border',
              value === v
                ? v
                  ? 'bg-amber-500 text-black border-amber-400'
                  : 'bg-slate-700 text-slate-100 border-slate-500'
                : 'bg-slate-800 text-slate-400 border-slate-600 hover:bg-slate-700',
            )}
          >
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </div>
  );
}

function Chips({
  options, selected, disabled, onToggle,
}: {
  options: string[]; selected: Set<string>; disabled: boolean; onToggle: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.has(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            disabled={disabled}
            className={cn(
              'px-2 py-1 text-xs border transition-colors',
              on
                ? 'bg-blue-600 text-white border-blue-400'
                : 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700',
            )}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function IndicatorRow({
  label, selected, disabled, onToggle,
}: {
  label: string; selected: boolean; disabled: boolean; onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'flex items-center gap-2 w-full border px-2.5 py-1.5 text-left text-xs leading-snug transition-colors',
        selected
          ? 'border-amber-500 bg-amber-950/40 text-amber-100'
          : 'border-slate-700 bg-slate-900/70 text-slate-200 hover:bg-slate-800',
      )}
    >
      <span
        className={cn(
          'flex h-3.5 w-3.5 items-center justify-center border text-[9px] font-black shrink-0',
          selected ? 'border-amber-400 bg-amber-500 text-black' : 'border-slate-500 bg-slate-800',
        )}
      >
        {selected ? '✓' : ''}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  );
}

function toLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
